import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  Send,
  DeleteOutline,
  Hub as HubIcon,
  MenuBook as KBIcon,
  TrendingUp as SalesIcon,
  Search as ResearchIcon,
  Mic as MeetingsIcon,
  Assignment as DeliveryIcon,
  SmartToy as AgentIcon,
} from "@mui/icons-material";
import { useAgents } from "@/hooks/useAgents";
import apiClient from "@/api/client";
import type { Agent } from "@/types";
import { brand } from "@/theme";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const AGENT_ICONS: Record<string, React.ReactNode> = {
  "AI-00": <HubIcon />,
  "AI-01": <KBIcon />,
  "AI-02": <SalesIcon />,
  "AI-03": <ResearchIcon />,
  "AI-04": <MeetingsIcon />,
  "AI-05": <DeliveryIcon />,
};

const AGENT_COLORS: Record<string, string> = {
  "AI-00": brand.forestGreen,
  "AI-01": "#2E7D32",
  "AI-02": "#C4922A",
  "AI-03": "#1565C0",
  "AI-04": "#6A1B9A",
  "AI-05": "#E65100",
};

function MessageBubble({ message, agent }: { message: ChatMessage; agent: Agent | undefined }) {
  const isUser = message.role === "user";
  const color = agent ? (AGENT_COLORS[agent.code] ?? brand.forestGreen) : brand.forestGreen;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 1.5,
      }}
    >
      {!isUser && agent && (
        <Avatar
          sx={{
            bgcolor: color,
            width: 32,
            height: 32,
            mr: 1,
            mt: 0.5,
            fontSize: "0.8rem",
          }}
        >
          {AGENT_ICONS[agent.code] ?? <AgentIcon />}
        </Avatar>
      )}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1.5,
          maxWidth: "75%",
          bgcolor: isUser ? brand.forestGreen : "#f0f0f0",
          color: isUser ? "#fff" : "text.primary",
          borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {message.content}
        </Typography>
      </Paper>
    </Box>
  );
}

export default function Sandbox() {
  const { data: agents, isLoading: agentsLoading } = useAgents();
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const selectedAgent = agents?.find((a) => a.id === selectedAgentId);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Reset conversation when agent changes
  useEffect(() => {
    setMessages([]);
  }, [selectedAgentId]);

  const chatMutation = useMutation({
    mutationFn: async (newMessages: ChatMessage[]) => {
      const res = await apiClient.post<{ response: string }>(`/sandbox/${selectedAgentId}/chat`, {
        messages: newMessages,
      });
      return res.data.response;
    },
    onSuccess: (response) => {
      setMessages((prev) => [...prev, { role: "assistant", content: response }]);
    },
  });

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || !selectedAgentId || chatMutation.isPending) return;

    const newMessages: ChatMessage[] = [...messages, { role: "user", content: trimmed }];
    setMessages(newMessages);
    setInput("");
    chatMutation.mutate(newMessages);
  };

  const handleClear = () => {
    setMessages([]);
    chatMutation.reset();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 100px)" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h4">Agent Sandbox</Typography>
        <Chip label="Ephemeral" size="small" variant="outlined" color="warning" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Test an agent&apos;s personality by chatting with it. Conversations are not saved to agent
        memory.
      </Typography>

      {/* Agent selector */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Select Agent</InputLabel>
          <Select
            value={selectedAgentId}
            label="Select Agent"
            onChange={(e) => setSelectedAgentId(e.target.value)}
          >
            {agentsLoading && <MenuItem disabled>Loading agents...</MenuItem>}
            {agents?.map((agent) => (
              <MenuItem key={agent.id} value={agent.id} aria-label={`${agent.code}: ${agent.name}`}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: AGENT_COLORS[agent.code] ?? brand.forestGreen,
                      width: 24,
                      height: 24,
                      fontSize: "0.7rem",
                    }}
                  >
                    {AGENT_ICONS[agent.code] ?? <AgentIcon sx={{ fontSize: 14 }} />}
                  </Avatar>
                  {agent.code}: {agent.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedAgent && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ alignSelf: "center", fontStyle: "italic" }}
          >
            &ldquo;{selectedAgent.tagline}&rdquo; — {selectedAgent.model_id}
          </Typography>
        )}

        <Box sx={{ flex: 1 }} />
        {messages.length > 0 && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<DeleteOutline />}
            onClick={handleClear}
          >
            Clear Chat
          </Button>
        )}
      </Box>

      {/* Chat area */}
      <Card
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 2,
            bgcolor: "#fafafa",
          }}
        >
          {!selectedAgentId && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Typography color="text.secondary">
                Select an agent above to start a sandbox conversation.
              </Typography>
            </Box>
          )}

          {selectedAgentId && messages.length === 0 && !chatMutation.isPending && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: AGENT_COLORS[selectedAgent?.code ?? ""] ?? brand.forestGreen,
                  width: 48,
                  height: 48,
                }}
              >
                {AGENT_ICONS[selectedAgent?.code ?? ""] ?? <AgentIcon />}
              </Avatar>
              <Typography variant="h6">{selectedAgent?.name}</Typography>
              <Typography color="text.secondary" variant="body2">
                Start chatting to test this agent&apos;s personality.
              </Typography>
            </Box>
          )}

          {messages.map((msg, i) => (
            <MessageBubble key={i} message={msg} agent={selectedAgent} />
          ))}

          {chatMutation.isPending && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 5 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {selectedAgent?.name} is thinking...
              </Typography>
            </Box>
          )}

          {chatMutation.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {chatMutation.error instanceof Error
                ? chatMutation.error.message
                : "Failed to get response"}
            </Alert>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input */}
        {selectedAgentId && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              p: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <TextField
              fullWidth
              placeholder={`Message ${selectedAgent?.name ?? "agent"}...`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              multiline
              maxRows={4}
              size="small"
              disabled={chatMutation.isPending}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!input.trim() || chatMutation.isPending}
              sx={{
                bgcolor: brand.forestGreen,
                color: "#fff",
                "&:hover": { bgcolor: "#153025" },
                "&.Mui-disabled": { bgcolor: "#ccc" },
                alignSelf: "flex-end",
              }}
            >
              <Send />
            </IconButton>
          </Box>
        )}
      </Card>
    </Box>
  );
}
